import { Button, Label, Layout, Select, TextField } from '@loadsmart/loadsmart-ui'
import { Selectable } from '@loadsmart/loadsmart-ui/dist/hooks/useSelectable'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  CircularProgress,
  Typography,
  Alert,
} from '@mui/material'
import { useState, useEffect } from 'react'
import FileViewer from 'molecules/FileViewer/FileViewer'
import { updateDataModelSchemaResource } from 'services/kraken-core/data_model/dataModel.service'
import {
  DATA_MODEL_SCHEMA_RESOURCE_KINDS,
  DataModelSchemaResourceKind,
} from 'common/types/kraken-core/DataModelSchemaResource'
import { isEditableContentType } from 'common/helpers/contentType'

const CONTENT_TYPE_OPTIONS = [
  { value: 'application/EDI-X12', label: 'X12' },
  { value: 'text/plain', label: 'Text' },
  { value: 'text/csv', label: 'CSV' },
  { value: 'application/json', label: 'JSON' },
  { value: 'application/xml', label: 'XML' },
  { value: 'application/yaml', label: 'YAML' },
  { value: 'text/yaml', label: 'YAML (Text)' },
  { value: 'text/markdown', label: 'Markdown' },
  { value: 'application/octet-stream', label: 'Binary' },
  { value: 'application/pdf', label: 'PDF' },
] as const

export interface ResourceEditDialogProps {
  open: boolean
  onClose: () => void
  resourceId: string
  initialName: string
  initialKind: DataModelSchemaResourceKind
  content: string
  contentType: string
  onSave: () => void
  hideKindField?: boolean
  isLoadingContent?: boolean
}

export default function ResourceEditDialog(props: ResourceEditDialogProps) {
  const [name, setName] = useState(props.initialName)
  const [kind, setKind] = useState<DataModelSchemaResourceKind>(props.initialKind)
  const [content, setContent] = useState(props.content)
  const [contentType, setContentType] = useState(props.contentType)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    setName(props.initialName)
    setKind(props.initialKind)
    setContent(props.content)
    setContentType(props.contentType)
  }, [props.initialName, props.initialKind, props.content, props.contentType])

  const handleSave = async () => {
    setIsSaving(true)
    try {
      const isEditable = isEditableContentType(contentType)

      if (isEditable) {
        // For editable files, create a File object from the content string
        const contentBlob = new Blob([content], { type: contentType })
        const file = new File([contentBlob], name, { type: contentType })

        await updateDataModelSchemaResource(props.resourceId, {
          name,
          kind,
          file,
          content_type: contentType,
        })
      } else {
        // For non-editable files, only update the metadata (name, kind, content_type)
        // without changing the file content
        await updateDataModelSchemaResource(props.resourceId, {
          name,
          kind,
          content_type: contentType,
        })
      }

      props.onSave()
      props.onClose()
    } catch (error) {
      console.error('Failed to update resource:', error)
    } finally {
      setIsSaving(false)
    }
  }

  const isEditable = isEditableContentType(contentType)

  return (
    <Dialog open={props.open} onClose={props.onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEditable ? 'Edit Resource' : 'Edit Resource Properties'}</DialogTitle>
      <DialogContent sx={{ py: 2, px: 3, overflowY: 'visible' }}>
        <Layout.Stack space="m">
          <Layout.Stack space="xs">
            <Label htmlFor="resource-name">Name</Label>
            <TextField id="resource-name" value={name} onChange={e => setName(e.target.value)} />
          </Layout.Stack>

          {!props.hideKindField && (
            <Layout.Group>
              <Layout.Stack space="xs">
                <Label htmlFor="resource-kind">Type</Label>
                <Select
                  id="resource-kind"
                  name="resource-kind"
                  options={[
                    { label: 'Validation', value: DATA_MODEL_SCHEMA_RESOURCE_KINDS.VALIDATION },
                    { label: 'Knowledge', value: DATA_MODEL_SCHEMA_RESOURCE_KINDS.KNOWLEDGE },
                  ]}
                  value={
                    {
                      value: kind,
                      label:
                        kind === DATA_MODEL_SCHEMA_RESOURCE_KINDS.KNOWLEDGE
                          ? 'Knowledge'
                          : 'Validation',
                    } as Selectable
                  }
                  onChange={e => {
                    const value = e.target.value as any
                    setKind(value.value as DataModelSchemaResourceKind)
                  }}
                />
              </Layout.Stack>

              <Layout.Stack space="xs">
                <Label htmlFor="content-type">Content Type</Label>
                <Select
                  id="content-type"
                  name="content-type"
                  options={CONTENT_TYPE_OPTIONS.map(option => ({
                    label: option.label,
                    value: option.value,
                  }))}
                  value={
                    {
                      value: contentType,
                      label:
                        CONTENT_TYPE_OPTIONS.find(opt => opt.value === contentType)?.label ||
                        contentType,
                    } as Selectable
                  }
                  onChange={e => {
                    const value = e.target.value as any
                    setContentType(value.value)
                  }}
                />
              </Layout.Stack>
            </Layout.Group>
          )}

          {props.isLoadingContent ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: window.innerHeight * 0.45,
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                backgroundColor: '#fafafa',
              }}
            >
              <CircularProgress size={40} sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary">
                Loading file content...
              </Typography>
            </Box>
          ) : isEditableContentType(contentType) ? (
            <FileViewer
              contentType={contentType}
              content={content}
              showOptions={false}
              onChange={setContent}
              options={{
                heigth: window.innerHeight * 0.45,
                readonly: false,
                autoPrettify: false,
              }}
            />
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: window.innerHeight * 0.45,
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                backgroundColor: '#fafafa',
              }}
            >
              <Alert severity="info" sx={{ mb: 2 }}>
                Content editing is not available for this file type. You can still edit the resource
                name, type, and content type.
              </Alert>
              <Typography variant="body2" color="text.secondary" align="center">
                File: {props.initialName}
              </Typography>
              <Typography variant="body2" color="text.secondary" align="center">
                Content Type: {contentType}
              </Typography>
            </Box>
          )}
        </Layout.Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="secondary" onClick={props.onClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} loading={isSaving}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}
